import { useEffect } from 'react';

import { useAuthContext } from 'lib/core/context/AuthProvider';

import { useAgentContext } from 'lib/common/contexts/AgentContext';

import CONTACT_STATES from 'lib/common/constants/contactStates';
import CONTACT_TYPES from 'lib/common/constants/contactTypes';

import { TConfig } from 'lib/common/types/Config';
import TFetch from 'lib/common/types/Fetch';
import TTask from 'lib/common/types/Task';
import connectGetter from 'lib/common/utils/connectGetter';

import THandleContactChange from '../types/HandleContactChange';
import destroyTask from '../utils/destroyTask';
import handleConnectedContact from '../utils/handleConnectedContact';
import useMoveTaskToACW from './useMoveTaskToACW';

type TArgs = {
  getTasks: () => TTask[];
  handleContactChange: THandleContactChange;
  fetch_: TFetch;
  config: TConfig;
  tokens?: any;
};

export default function useTaskRefresh({ getTasks, handleContactChange, fetch_, config, tokens }: TArgs) {
  const { getUser } = useAuthContext();
  const { agent } = useAgentContext();
  const moveTaskToACW = useMoveTaskToACW({ handleContactChange });

  useEffect(() => {
    if (!agent) {
      return;
    }

    agent.onRefresh(() => {
      // Task refresh only needs to handle chats + agent tasks
      const tasks = getTasks().filter((task) => task.type === CONTACT_TYPES.TASK || task.type === CONTACT_TYPES.CHAT);
      const existingContacts = connectGetter(agent, 'getContacts') || [];

      tasks.forEach(({ contact: taskContact, taskId, status }) => {
        const existingContact = existingContacts.find((contact) => contact.contactId === taskId);

        if (!existingContact) {
          return destroyTask({ contact: taskContact, handleContactChange });
        }

        const existingContactState = connectGetter(existingContact, 'getState')?.type;
        const taskIsConnecting = status === CONTACT_STATES.CONNECTING;
        const taskIsPaused = status === CONTACT_STATES.PAUSED;
        const taskIsConnected = status === CONTACT_STATES.CONNECTED;

        if (taskIsConnecting && existingContactState === connect.ContactStateType.CONNECTED) {
          return handleConnectedContact({ contact: taskContact, handleContactChange, fetch_, config, tokens, getUser });
        }

        if (taskIsConnecting && existingContactState === connect.ContactStateType.REJECTED) {
          return handleContactChange({ contact: taskContact, status: CONTACT_STATES.REJECTED });
        }

        if (taskIsConnected && existingContactState === connect.ContactStateType.PAUSED) {
          return handleContactChange({ contact: taskContact, status: CONTACT_STATES.CONNECTED });
        }

        if (taskIsPaused && existingContactState === connect.ContactStateType.CONNECTED) {
          return handleContactChange({ contact: taskContact, status: CONTACT_STATES.PAUSED });
        }

        if (!(status === CONTACT_STATES.CONNECTED && existingContactState === connect.ContactStateType.ENDED)) {
          return;
        }

        moveTaskToACW(taskContact);
      });
    });
  }, [agent]);
}
